import { t } from 'i18next';

import { GlobalHelper } from './GlobalHelper';
import { DialogActions } from '../actions/DialogActions';
import { ViewSettingsActions } from '../actions/ViewSettingsActions';
import { ProjectManagerStore } from '../stores/ProjectManagerStore';
import { Stats } from './Stats';
import { PersistentStore } from '../stores/PersistentStore';
import { ScreenshotHelper } from './ScreenshotHelper';
import { DownloadImageSize } from '../constants/LayoutConstants';
import { ViewSettingsStore } from '../stores/ViewSettingsStore';
import { UIHelper } from './UIHelper';
import { FileFormats } from '../constants/FileFormat';
import { ExportGeometryFormat, ExportFormat } from '../constants/ExportFormats';

import cf = egr.wcf.cf;
import utils = egr.wcf.utils;
import eaiws = egr.wcf.eaiws;

declare var download: (content: string | Blob, filename?: string, mimetype?: string) => void;

interface OldNavigator {
    msSaveBlob?: (data: Blob, filename: string) => void;
    msSaveOrOpenBlob?: (data: Blob, filename: string) => void;
}

interface ExportableMainArticles {
    exportable: Array<cf.MainArticleElement>;
    notExportable: Array<cf.MainArticleElement>;
}

export abstract class DownloadHelper {
    public static exportObk(): Promise<string> {
        return GlobalHelper.articleManager.saveToObk(true);
    }

    public static exportObx(): Promise<string> {
        return GlobalHelper.articleManager.exportObx(GlobalHelper.articleManager.getAllMainArticles());
    }

    public static async exportPBox(): Promise<string> {
        const buffer: ArrayBuffer = await DownloadHelper.getProjectContainer();
        return DownloadHelper.uploadContentToFileStorage(
            utils.arraybuffer.toBase64(buffer),
            ProjectManagerStore.getProjectName(true),
            'pbox'
        );
    }

    public static async exportGeometries(exports: Array<Array<string>>): Promise<Array<string>> {
        // Note: this function needs better error handling
        try {
            // get main articles
            const tArticles: ExportableMainArticles = await DownloadHelper.getExportableMainArticles();
            if (tArticles.exportable.length === 0) {
                return Promise.reject('no exportable articles');
            }
            if (tArticles.notExportable.length > 0) {
                const continueExport: boolean = await DialogActions.getPromiseDialog(
                    t('Not all articles can be exported.')
                );
                if (!continueExport) {
                    return Promise.reject(null);
                }
            }

            // export obx
            const tObxUrl: string = await GlobalHelper.articleManager.exportObx(tArticles.exportable);

            // create temporary set article
            const tFolderId: string = await GlobalHelper.eaiwsSession.basket.insertFolder(null, null, 'Set');
            const tSetId: string = await GlobalHelper.eaiwsSession.basket.convertToSetArticle(tFolderId);

            // insert obx into set article
            const tPastedIds: Array<string> = await GlobalHelper.eaiwsSession.basket.paste(tSetId, null, tObxUrl);

            // add to set article
            await GlobalHelper.eaiwsSession.basket.addToSetArticle(tPastedIds);

            // export geometries
            const exportTimeout: number = 180000;
            const tExportUrls: Array<string> = new Array<string>();
            for (const tExportOptions of exports) {
                const tExportUrl: string = await GlobalHelper.eaiwsSession.basket.getExportedGeometry(tSetId, tExportOptions, exportTimeout);
                tExportUrls.push(tExportUrl);
            }

            // delete temporary set
            const tOptions: eaiws.basket.DeleteItemsOptions = new eaiws.basket.DeleteItemsOptions();
            tOptions.subItems = true;
            await GlobalHelper.eaiwsSession.basket.deleteItems([tSetId], tOptions);

            // delete temporary obx
            await GlobalHelper.eaiwsSession.deleteFile(tObxUrl);

            return (tExportUrls);
        } catch (pError) {
            utils.Log.error('Failed to export geometry.', pError);
            return Promise.reject(pError);
        }
    }

    public static uploadUrlToFileStorage(url: string, filename: string): Promise<string> {
        const extension: string = url.substr(url.lastIndexOf('.') + 1);
        const eaiwsBaseurl: string = GlobalHelper.eaiwsSession.baseUrl;

        return utils.async.ajax(
            'POST',
            eaiwsBaseurl + '/EAIWS/plugins/Blacksmith/',
            JSON.stringify({
                input: url,
                tasks: [
                    {
                        name: 'FetchUrlContent'
                    },
                    {
                        name: 'UploadContent',
                        args: {
                            url: eaiwsBaseurl + '/EAIWS/plugins/FileStorage/' + filename + '.' + extension,
                        }
                    }
                ]
            }),
            {
                dataType: 'text',
                headers: { 'Content-Type': 'application/json; charset=utf-8' },
                timeout: 360000
            }).then((result: any): string => {
                return result as string;
            });
    }

    public static uploadContentToFileStorage(content: string, filename: string, extension: string): Promise<string> {
        // FixMe: upload binary content directly
        const eaiwsBaseurl: string = GlobalHelper.eaiwsSession.baseUrl;

        return utils.async.ajax(
            'POST',
            eaiwsBaseurl + '/EAIWS/plugins/Blacksmith/',
            JSON.stringify({
                input: content,
                tasks: [
                    {
                        name: 'Base64Converter',
                        args: {
                            mode: 'decode'
                        }
                    },
                    {
                        name: 'UploadContent',
                        args: {
                            url: eaiwsBaseurl + '/EAIWS/plugins/FileStorage/' + filename + '.' + extension,
                        }
                    }
                ]
            }),
            {
                dataType: 'text',
                headers: { 'Content-Type': 'application/json; charset=utf-8' },
                timeout: 360000
            }
        ).then((result: any): string => {
            return result as string;
        });
    }

    public static async downloadScreenshot(): Promise<void> {
        Stats.sendEvent({
            category: 'Download',
            action: 'download',
            label: PersistentStore.getState().imageFormat
        });

        const image: string = await ScreenshotHelper.getScreenshot({
            width: DownloadImageSize.width,
            height: DownloadImageSize.height,
            renderAsync: true,
            addWatermark: true,
            customMimetype: true
        });

        let filename: string = 'screenshot.png';

        if (PersistentStore.getState().imageFormat === 'JPG') {
            filename = 'screenshot.jpg';
        }

        if (ViewSettingsStore.getBrowser() === 'Safari') {
            DialogActions.showDownloadLink(
                URL.createObjectURL(DownloadHelper.dataURItoBlob(image)),
                filename
            );
        } else {
            download(image, filename);
        }
    }

    public static downloadUrl(url: string, addForceArg: boolean = true): void {
        UIHelper.insertBackgroundFrame(
            url + (addForceArg ? '?forcedownload=1' : ''),
            1000
        );
    }

    public static async getExportUrl(format: ExportFormat): Promise<string | null> {
        switch (format) {
            case 'OBX':
                return DownloadHelper.exportObx();
            case 'OBK':
                return DownloadHelper.exportObk();
            case 'DWG':
            case '3DS':
            case 'FBX':
            case 'Revit':
            case 'GLB':
            case 'USDZ':
                return DownloadHelper.exportGeometries([ExportGeometryFormat[format]]).then((urls: Array<string>): string => {
                    return urls[0];
                });
            default:
                return null;
        }
    }

    public static async downloadExport(format: ExportFormat): Promise<void> {
        if (['OBX', 'OBK'].indexOf(format) !== -1) {
            const continueExport: boolean = await DialogActions.inconsistencyDialog();
            if (!continueExport) {
                return;
            }
        }

        Stats.sendEvent({
            category: 'Download',
            action: 'download',
            label: format
        });

        ViewSettingsActions.withLock<void>(async () => {
            try {
                const uri: string | null = await DownloadHelper.getExportUrl(format);
                if (utils.string.isNotNullOrEmpty(uri)) {
                    if (ViewSettingsStore.getBrowser() === 'Safari') {
                        DialogActions.showDownloadLink(
                            uri,
                            ProjectManagerStore.getProjectName(true) + '.' + FileFormats[format].extension
                        );
                    } else {
                        DownloadHelper.downloadUrl(uri);
                    }
                } else {
                    UIHelper.showMessage(t('The CAD file could not be generated.'));
                }
            } catch {
                UIHelper.showMessage(t('The CAD file could not be generated.'));
            }
        });
    }

    public static downloadArrayBuffer(buffer: ArrayBuffer, filename: string, mimeType: string = 'application/octet-stream'): void {
        if (buffer == null) {
            return;
        }

        const blob: Blob = new Blob([buffer], {type: mimeType});
        DownloadHelper.downloadBlob(blob, filename);
    }

    public static downloadBlob(blob: Blob, filename: string): void {
        if ((navigator as OldNavigator).msSaveBlob != null) {
            // IE
            (navigator as OldNavigator).msSaveBlob!(blob, filename);
        } else if ((navigator as OldNavigator).msSaveOrOpenBlob != null) {
            // IE
            (navigator as OldNavigator).msSaveOrOpenBlob!(blob, filename);
        } else {
            const link: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
            link.style.display = 'none';
            document.body.appendChild(link);
            const url: string = URL.createObjectURL(blob);
            link.href = url;
            link.download = filename;
            link.click();
            window.setTimeout(
                () => {
                    URL.revokeObjectURL(url);
                },
                100
            );
            document.body.removeChild(link);
        }
    }

    public static async downloadProjectContainer(): Promise<void> {
        Stats.sendEvent({
            action: 'Save'
        });

        const buffer: ArrayBuffer = await DownloadHelper.getProjectContainer();

        DownloadHelper.downloadArrayBuffer(
            buffer,
            ProjectManagerStore.getProjectName(true) + '.pbox'
        );
    }

    public static async getContent(url: string): Promise<ArrayBuffer> {
        return await utils.async.ajax<ArrayBuffer>('GET', url, null, {
            responseType: 'arraybuffer'
        });
    }

    // http://stackoverflow.com/questions/12168909/blob-from-dataurl
    private static dataURItoBlob(dataURI: string): Blob {
        const byteString: string = atob(dataURI.split(',')[1]);
        // separate out the mime component
        const mimeString: string = dataURI.split(',')[0].split(':')[1].split(';')[0];
        // write the bytes of the string to an ArrayBuffer
        const ab: ArrayBuffer = new ArrayBuffer(byteString.length);
        const ia: Uint8Array = new Uint8Array(ab);
        for (let i: number = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        // write the ArrayBuffer to a blob, and you're done
        return new Blob([ab], {type: mimeString});
    }

    private static async getProjectContainer(embedResources: boolean = true): Promise<ArrayBuffer> {
        return GlobalHelper.coreApp.document.saveToZip({
            embedGeometries: embedResources,
            embedTextures: embedResources
        });
    }

    private static async getExportableMainArticles(): Promise<ExportableMainArticles> {
        const tResult: ExportableMainArticles = {
            exportable: new Array(),
            notExportable: new Array(),
        };

        // sync session
        await GlobalHelper.articleManager.synchronizeSession(true);

        // get main articles
        const tArticles: Array<cf.MainArticleElement> = GlobalHelper.articleManager.getAllMainArticles();
        if (tArticles.length === 0) {
            return (tResult);
        }

        // analyze known update state
        const tUnknownState: Map<string, cf.MainArticleElement> = new Map();
        const tUnknownStateIds: Array<string> = new Array();
        for (const tArticle of tArticles) {
            // user articles currently can't be exported
            if (tArticle instanceof (cf.UserArticleElement)) {
                tResult.notExportable.push(tArticle);
            } else if (utils.string.isNotNullOrEmpty(tArticle.basketId)) {
                const tState: eaiws.basket.OFMLUpdateState = tArticle.getKnownOfmlUpdateState();
                if (tState === 'UpToDate' || tState === 'Updatable') {
                    tResult.exportable.push(tArticle);
                } else if (tState === 'Unknown') {
                    tUnknownState.set(tArticle.basketId, tArticle);
                    tUnknownStateIds.push(tArticle.basketId);
                } else {
                    // TODO: how to handle migratable articles?
                    tResult.notExportable.push(tArticle);
                }
            }
        }

        // get state from eaiws for articles with unknown state
        if (tUnknownStateIds.length > 0) {
            const tOptions: eaiws.basket.UpdateBasketArticlesOptions = new eaiws.basket.UpdateBasketArticlesOptions();
            tOptions.migrate = false;
            tOptions.update = false;
            const tEaiwsStates: Array<eaiws.basket.UpdateBasketArticleResult> = await GlobalHelper.eaiwsSession.basket.updateBasketArticles(
                tUnknownStateIds, null, tOptions);
            if (tResult != null) {
                for (const tState of tEaiwsStates) {
                    const articleElement: cf.MainArticleElement | undefined = tUnknownState.get(tState.itemId);
                    if (articleElement == null) {
                        continue;
                    }

                    if (tState.updateState === 'UpToDate' ||
                        tState.updateState === 'Updatable') {
                        tResult.exportable.push(articleElement);
                    } else {
                        // TODO: how to handle migratable articles?
                        tResult.notExportable.push(articleElement);
                    }
                }
            }
        }

        return (tResult);
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/DownloadHelper.ts